// src/new/components/monitor/TriageChat/followUps.ts

const MAX_FOLLOW_UPS = 4;
// Follow-ups are full sentences, not short labels. We only guard against a
// runaway paragraph — and even then we cut on a word boundary with an ellipsis,
// never mid-word — so normal-length suggestions pass through untouched.
const MAX_LEN = 240;

function clean(s: string): string {
  const collapsed = s
    .replace(/\*\*/g, '') // bold markers
    .replace(/[*_`]/g, '') // stray emphasis/code markers
    .replace(/\s+/g, ' ') // collapse newlines / runs of whitespace
    .trim();
  if (collapsed.length <= MAX_LEN) return collapsed;
  const cut = collapsed.slice(0, MAX_LEN);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Given the final assistant message, return up to 4 short suggested follow-up
 * actions to surface as clickable chips. Pure; returns [] when nothing
 * actionable is found.
 *
 * Heuristics (in order):
 *  1. Trailing question ('?') + a markdown list → the list items become chips.
 *  2. Final question offering " or " options → split into the options.
 *  3. Otherwise → no follow-ups.
 */
export function extractFollowUps(text: string): string[] {
  if (!text) return [];
  const trimmed = text.trimEnd();
  const hasTrailingQuestion = trimmed.endsWith('?');

  // 1. Trailing question + markdown list items.
  if (hasTrailingQuestion) {
    const items: string[] = [];
    for (const line of text.split('\n')) {
      const m = line.match(/^\s*(?:[-*+]|\d+[.)])\s+(.*\S)\s*$/);
      if (m) {
        const item = clean(m[1]);
        if (item) items.push(item);
      }
    }
    if (items.length > 0) return items.slice(0, MAX_FOLLOW_UPS);
  }

  // 2. "Would you like me to A or B?" style options.
  if (hasTrailingQuestion) {
    // Take the last sentence ending in '?'.
    const sentences = trimmed.split(/(?<=[.!?])\s+/);
    const lastQuestion = sentences[sentences.length - 1] ?? trimmed;
    if (/\bor\b/i.test(lastQuestion)) {
      // Strip a leading lead-in like "Would you like me to" up to the options.
      const body = lastQuestion.replace(/\?+\s*$/, '');
      const lead = body.match(/^(.*?\b(?:to|should I|want me to|like me to)\b\s*)/i);
      const optionsStr = lead ? body.slice(lead[0].length) : body;
      const options = optionsStr
        .split(/\s*,?\s+or\s+|\s*,\s*/i)
        .map((o) => clean(o))
        .filter(Boolean);
      if (options.length >= 2) return options.slice(0, MAX_FOLLOW_UPS);
    }
  }

  return [];
}
